import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { loginRequest } from '../api/loginApi'
import { persistAuthSession } from '../utils/authSession'
import { requestForgotPasswordOtp } from '../../password-recovery/api/passwordRecoveryApi'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function extractToken(responseData) {
  return responseData?.token || responseData?.access_token || null
}

function extractUser(responseData) {
  return responseData?.user || null
}

export function useLoginScreen() {
  const navigate = useNavigate()
  const location = useLocation()

  // Tenant username can be pre-filled when navigating from a successful signup
  const [tenantUsername, setTenantUsername] = useState(
    location.state?.tenantUsername || localStorage.getItem('rememberTenantUsername') || ''
  )
  const [identifier, setIdentifier] = useState(localStorage.getItem('rememberEmail') || '')
  const [password, setPassword] = useState('')
  const [rememberDevice, setRememberDevice] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState(location.state?.message || '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pendingAgentVerification, setPendingAgentVerification] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    const reason = String(params.get('reason') || '').toLowerCase()
    if (reason === 'session-replaced') {
      setSuccessMessage('Your previous login session was replaced by a newer login.')
    } else if (reason === 'no-session') {
      setSuccessMessage('Your previous session is no longer active. Please login again.')
    }
  }, [location.search])

  useEffect(() => {
    const stateEmail = location.state?.email
    if (stateEmail) {
      setIdentifier(stateEmail)
    }
    // Pre-fill tenant username if navigated from signup success
    const stateTenantUsername = location.state?.tenantUsername
    if (stateTenantUsername) {
      setTenantUsername(String(stateTenantUsername).toUpperCase())
    }
  }, [location.state])

  async function handleLoginSubmit(event) {
    event.preventDefault()
    setError('')

    // ── Client-side validation ────────────────────────────────────────────────
    if (!tenantUsername.trim()) {
      setError('Enter your Tenant Username (e.g. BOSS0001) to continue.')
      return
    }

    if (!identifier.trim()) {
      setError('Enter your official email to continue.')
      return
    }

    const looksLikeEmail = identifier.includes('@')
    if (looksLikeEmail && !EMAIL_REGEX.test(identifier)) {
      setError('Enter a valid email address format.')
      return
    }

    if (password.trim().length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await loginRequest({
        tenantUsername: tenantUsername.trim().toUpperCase(),
        identifier,
        password,
        rememberDevice,
      })

      if (response?.user?.first_login) {
        navigate('/auth/password-reset', {
          replace: true,
          state: {
            tenantUsername: tenantUsername.trim().toUpperCase(),
            email: identifier.trim().toLowerCase(),
            currentPassword: password,
            mode: 'first-login',
          },
        })
        return
      }


      if (response?.require_agent_verification) {
        if (response?.requires_session_resolution) {
          setPendingAgentVerification({
            nonce: response?.nonce ?? null,
            user: response?.user ?? null,
            agentPortRange: response?.agent_port_range ?? null,
            rememberMe: rememberDevice,
            currentPassword: password,
            sessionResolution: response?.session_resolution || null,
          })
          return
        }
        navigate('/system-check', {
          replace: true,
          state: {
            nonce: response?.nonce ?? null,
            user: response?.user ?? null,
            agentPortRange: response?.agent_port_range ?? null,
            rememberMe: rememberDevice,
            currentPassword: password,
            sessionResolutionAction: null,
          },
        })
        return
      }

      persistAuthSession({
        token: extractToken(response),
        user: extractUser(response),
        rememberDevice,
        identifier,
      })

      // Remember the tenant username if rememberDevice is checked
      if (rememberDevice) {
        localStorage.setItem('rememberTenantUsername', tenantUsername.trim().toUpperCase())
      } else {
        localStorage.removeItem('rememberTenantUsername')
      }

      navigate(response?.redirect_to || response?.redirectTo || '/dashboard/personal', {
        replace: true,
      })
    } catch (requestError) {
      setError(
        extractApiErrorMessage(requestError, 'Login failed. Please verify credentials and try again.')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  function cancelSessionResolution() {
    setPendingAgentVerification(null)
  }

  function continueWithSessionResolution(action) {
    if (!pendingAgentVerification) return
    setPendingAgentVerification(null)
    navigate('/system-check', {
      replace: true,
      state: {
        ...pendingAgentVerification,
        sessionResolutionAction: action,
      },
    })
  }

  async function handleForgotPasswordClick() {
    setError('')
    setSuccessMessage('')

    const normalizedTenantUsername = tenantUsername.trim().toUpperCase()
    if (!normalizedTenantUsername) {
      setError('Enter your Tenant Username (e.g. BOSS0001) before continuing to verification.')
      return
    }

    const normalizedEmail = identifier.trim().toLowerCase()
    if (!normalizedEmail) {
      setError('Enter your official email before continuing to verification.')
      return
    }

    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setError('Enter a valid official email address before requesting OTP.')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await requestForgotPasswordOtp({
        tenantUsername: normalizedTenantUsername,
        email: normalizedEmail,
      })
      navigate('/auth/otp-verification', {
        replace: true,
        state: {
          tenantUsername: normalizedTenantUsername,
          email: normalizedEmail,
          message: response?.message || 'Verification code sent. Check your inbox.',
        },
      })
    } catch (requestError) {
      setError(
        extractApiErrorMessage(
          requestError,
          'Unable to start password recovery for this email address.'
        )
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    tenantUsername,
    identifier,
    password,
    rememberDevice,
    isSubmitting,
    error,
    successMessage,
    setTenantUsername,
    setIdentifier,
    setPassword,
    setRememberDevice,
    handleLoginSubmit,
    handleForgotPasswordClick,
    pendingAgentVerification,
    cancelSessionResolution,
    continueWithSessionResolution,
  }
}
